import { supabase } from '@/integrations/supabase/client'
import { VinculoResolver, type VinculoResolvido } from './vinculoResolver'

export interface RelacionadorMetricas {
  relacionadorId: string
  nome: string
  reunioesTotal: number
  reunioesEste: number
  produzidoTotal: number
  produzidoVinculado: number
  produzidoSemVinculo: number
  comissaoEstimada: number | null
  metaVigente: number | null
  percentualMeta: number | null
}

export interface SupervisaoEquipeData {
  supervisor: {
    id: string
    nome: string
  }
  periodo: {
    mes: number
    ano: number
  }
  resumo: {
    equipeTamanho: number
    reunioesTotal: number
    produzidoTotal: number
    produzidoVinculado: number
    contratosAguardandoVinculo: number
  }
  relacionadores: RelacionadorMetricas[]
  vinculos: VinculoResolvido[]
}

export class SupervisaoVendasService {
  /**
   * Busca dados de supervisão para um supervisor em um período.
   * Integração com dados reais (Fase 2).
   */
  static async getEquipeVendas(
    supervisorId: string,
    mes: number,
    ano: number
  ): Promise<SupervisaoEquipeData> {
    try {
      // 1. BUSCAR SUPERVISOR
      // supervisorId pode vir como UUID string ou como ID numérico (bigint) do AuthContext
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(supervisorId)
      
      let query = supabase
        .from('consultores')
        .select('id, user_id, nome, tenant_id')

      if (isUuid) {
        query = query.eq('user_id', supervisorId)
      } else {
        const numericId = Number(supervisorId)
        if (isNaN(numericId)) {
          throw new Error(`supervisorId inválido: ${supervisorId}`)
        }
        query = query.eq('id', numericId)
      }

      const { data: supervisorData, error: supervisorError } = await query.single()

      if (supervisorError || !supervisorData) {
        throw new Error(`Supervisor não encontrado para o supervisorId: ${supervisorId}`)
      }

      // 2. BUSCAR EQUIPE DO SUPERVISOR
      // Consultores que possuem este supervisor como supervisor_id
      const { data: equipeData, error: equipeError } = await supabase
        .from('consultores')
        .select('id, user_id, nome')
        .eq('supervisor_id', supervisorData.id)

      if (equipeError) {
        throw equipeError
      }

      const equipeCIds = equipeData?.map(c => c.id) || []

      if (equipeCIds.length === 0) {
        return {
          supervisor: { id: supervisorId, nome: supervisorData.nome || 'Sem nome' },
          periodo: { mes, ano },
          resumo: {
            equipeTamanho: 0,
            reunioesTotal: 0,
            produzidoTotal: 0,
            produzidoVinculado: 0,
            contratosAguardandoVinculo: 0,
          },
          relacionadores: [],
          vinculos: [],
        }
      }

      // 3. CONFIGURAR DATAS DO PERÍODO
      const startMonthStr = `${ano}-${mes.toString().padStart(2, '0')}-01`
      const nextMonth = mes === 12 ? 1 : mes + 1
      const nextYear = mes === 12 ? ano + 1 : ano
      const endMonthStr = `${nextYear}-${nextMonth.toString().padStart(2, '0')}-01`

      // 4. BUSCAR AGENDAMENTOS DA EQUIPE
      const { data: agendamentos, error: agendamentosError } = await supabase
        .from('agendamentos')
        .select('id, cliente_id, relacionador_id, data_agendamento')
        .in('relacionador_id', equipeCIds)
        .gte('data_agendamento', startMonthStr)
        .lt('data_agendamento', endMonthStr)

      if (agendamentosError) {
        throw agendamentosError
      }

      // 5. BUSCAR CONTRATOS DA EQUIPE
      const { data: contratos, error: contratosError } = await supabase
        .from('contratos')
        .select('id, cliente_id, relacionador_id, created_at, valor_bem')
        .eq('tenant_id', supervisorData.tenant_id)
        .gte('created_at', startMonthStr)
        .lt('created_at', endMonthStr)

      if (contratosError) {
        throw contratosError
      }

      // 6. BUSCAR PIPELINE CARDS para resolução de vínculo por cliente_id
      const { data: pipelineCards, error: pipelineCardsError } = await supabase
        .from('pipeline_cards')
        .select('id, responsavel_id, cliente_id')

      if (pipelineCardsError) {
        throw pipelineCardsError
      }

      // 7. BUSCAR CONSULTORES para mapeamento UUID → nome
      const { data: consultores, error: consultoresError } = await supabase
        .from('consultores')
        .select('id, user_id, nome')

      if (consultoresError) {
        throw consultoresError
      }

      // 8. RESOLVER VÍNCULOS
      const pipelineCardsByClienteMap = new Map<string, any>(
        (pipelineCards || []).map(p => [String(p.cliente_id), p])
      )
      const consultoresMap = new Map<string, { id: string; user_id: string; nome: string }>(
        (consultores || [])
          .filter(c => c.user_id)
          .map(c => [c.user_id as string, {
            id: String(c.id),
            user_id: c.user_id as string,
            nome: c.nome || 'Sem nome'
          }])
      )

      const vinculosResolvidos = await VinculoResolver.resolverBatch(
        (contratos || []).map(c => {
          const card = pipelineCardsByClienteMap.get(String(c.cliente_id))
          return {
            id: String(c.id),
            cliente_id: String(c.cliente_id),
            relacionador_id: c.relacionador_id ? String(c.relacionador_id) : null,
            pipeline_card_id: card ? card.id : null,
            valor_bem: c.valor_bem,
            created_at: c.created_at,
          }
        }),
        (agendamentos || []).map(a => ({
          id: String(a.id),
          cliente_id: String(a.cliente_id),
          relacionador_id: a.relacionador_id ? String(a.relacionador_id) : '',
        })),
        new Map((pipelineCards || []).map(p => [p.id, p])),
        consultoresMap
      )

      // 9. BUSCAR COMISSÕES DO PERÍODO
      const { data: comissoes, error: comissoesError } = await supabase
        .from('comissoes')
        .select('consultor_id, valor_comissao')
        .eq('periodo_mes', mes)
        .eq('periodo_ano', ano)
        .in('consultor_id', equipeCIds)

      if (comissoesError) {
        throw comissoesError
      }

      // 10. BUSCAR METAS DISTRIBUÍDAS DO PERÍODO
      const { data: metas, error: metasError } = await supabase
        .rpc('get_meta_distribuida_periodo', {
          p_tenant_id: supervisorData.tenant_id,
          p_periodo_mes: mes,
          p_periodo_ano: ano
        })

      if (metasError) {
        throw metasError
      }

      // 11. PROCESSAR MÉTRICAS DOS RELACIONADORES
      const metricas = this._processarMetricas(
        equipeCIds,
        agendamentos || [],
        contratos || [],
        vinculosResolvidos,
        consultores || [],
        comissoes || [],
        metas || []
      )

      // 12. CALCULAR RESUMO FINAL
      const equipeCIdsStr = equipeCIds.map(String)
      const resumo = {
        equipeTamanho: equipeCIds.length,
        reunioesTotal: agendamentos?.length || 0,
        produzidoTotal: vinculosResolvidos.filter(v => v.relacionadorId && equipeCIdsStr.includes(String(v.relacionadorId))).length,
        produzidoVinculado: vinculosResolvidos.filter(v => v.relacionadorId && equipeCIdsStr.includes(String(v.relacionadorId)) && v.origem !== 'SEM_VINCULO').length,
        contratosAguardandoVinculo: vinculosResolvidos.filter(v => v.origem === 'SEM_VINCULO').length,
      }

      return {
        supervisor: { id: supervisorId, nome: supervisorData.nome || 'Sem nome' },
        periodo: { mes, ano },
        resumo,
        relacionadores: metricas,
        vinculos: vinculosResolvidos,
      }
    } catch (error) {
      console.error('Erro ao buscar equipe de vendas:', error)
      throw error
    }
  }

  /**
   * Vincula manualmente um contrato a um relacionador (Fase 2).
   */
  static async vincularContrato(
    contratoId: string,
    relacionadorId: string
  ): Promise<void> {
    const { error } = await supabase
      .from('contratos')
      .update({ relacionador_id: Number(relacionadorId) })
      .eq('id', Number(contratoId))

    if (error) {
      throw error
    }
  }

  /**
   * Processa agregações e métricas individuais para cada consultor
   */
  private static _processarMetricas(
    equipeCIds: number[],
    agendamentos: any[],
    _contratos: any[],
    vinculos: VinculoResolvido[],
    consultores: any[],
    comissoes: any[],
    metas: any[]
  ): RelacionadorMetricas[] {
    const metricas = new Map<string, RelacionadorMetricas>()

    // Inicializar métricas para cada consultor na equipe
    for (const cId of equipeCIds) {
      const consultor = consultores.find(c => c.id === cId)
      metricas.set(String(cId), {
        relacionadorId: String(cId),
        nome: consultor?.nome || 'Sem nome',
        reunioesTotal: 0,
        reunioesEste: 0,
        produzidoTotal: 0,
        produzidoVinculado: 0,
        produzidoSemVinculo: 0,
        comissaoEstimada: null,
        metaVigente: null,
        percentualMeta: null,
      })
    }

    // Contar reuniões / agendamentos
    for (const agendamento of agendamentos) {
      const relIdStr = String(agendamento.relacionador_id)
      if (metricas.has(relIdStr)) {
        const m = metricas.get(relIdStr)!
        m.reunioesTotal++
        m.reunioesEste++
      }
    }

    // Contar contratos produzidos e vínculos resolutos
    for (const vinculo of vinculos) {
      if (vinculo.relacionadorId && metricas.has(vinculo.relacionadorId)) {
        const m = metricas.get(vinculo.relacionadorId)!
        m.produzidoTotal++
        if (vinculo.origem !== 'SEM_VINCULO') {
          m.produzidoVinculado++
        } else {
          m.produzidoSemVinculo++
        }
      }
    }

    // Adicionar comissões estimadas
    const comissoesMap = new Map(
      (comissoes || []).map(c => [String(c.consultor_id), Number(c.valor_comissao)])
    )
    metricas.forEach((m, cId) => {
      m.comissaoEstimada = comissoesMap.get(cId) || null
    })

    // Adicionar metas distribuídas vigentes e calcular percentuais
    const metasMap = new Map(
      (metas || []).map(m => [String(m.consultor_id), Number(m.valor_meta)])
    )
    metricas.forEach((m, cId) => {
      const meta = metasMap.get(cId)
      if (meta) {
        m.metaVigente = meta
        m.percentualMeta = m.produzidoTotal && meta ? (m.produzidoTotal / meta) * 100 : 0
      }
    })

    return Array.from(metricas.values())
  }
}
